import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable, { TableColumn } from "react-data-table-component";
import { NavbarApplicationInfo } from "../../components/CommonComponents/Navbar/NavbarApplicationInfo";
import {
  DownloadButton,
  ViewButton,
} from "../../components/CommonComponents/EditComponent/EditComponent";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { formatDate } from "../../utils/date";
import {
  TAccountantReceipt,
  useAccountantReceipt,
  useSaveAccountantReceipt,
} from "../../hooks/accountantReceipt.hooks";
import {
  useApplicationForLevel1,
} from "../../hooks/level-1-application-approval.hooks";
import { useLevel1DemandNoteDataForPayment } from "../../hooks/level-1-payment-approval.hooks";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Step 3 of the Accountant flow: having accepted the applicant's bank slip, the
 * Accountant issues the municipality's own official receipt here. The upload is
 * mandatory — the application sits in "Pending Accountant Receipt Upload" until
 * it lands, and only then moves to Level 1.
 *
 * The same screen handles a re-upload after Level 1, Level 2 or Level 3 bounced the
 * previous receipt back for correction; earlier receipts are kept and shown
 * read-only rather than replaced in place.
 */
const AccountantReceiptUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const applicationId = location.state?.id as number;

  const [file, setFile] = useState<File | null>(null);
  const [receiptNo, setReceiptNo] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [comment, setComment] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: application } = useApplicationForLevel1({ id: applicationId });
  const { data: receiptData } = useAccountantReceipt({ applicationId });
  const { data: demandNoteData } = useLevel1DemandNoteDataForPayment({
    applicationId,
  });

  const saveMutation = useSaveAccountantReceipt();
  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    if (application) {
      setStuff(
        <NavbarApplicationInfo
          applicationId={application.applicationId}
          status={application.status}
          applicationType={application.rl_application_type}
          currentPhase={application.current_phase}
        />
      );
    }
    return () => setStuff(null);
  }, [setStuff, application]);

  const totalDemandAmount = Number(
    (demandNoteData as any)?.totalAmount || demandNoteData?.totalAmount || 0
  );

  /**
   * A reviewer's rejection reason lives on the superseded receipt, so the
   * Accountant needs the previous rows visible while preparing the replacement.
   */
  const previousReceipts = receiptData?.history || [];
  const currentReceipt = receiptData?.current || null;

  const isCorrectionUpload = previousReceipts.length > 0 || !!currentReceipt;

  const previousReceiptColumns = useMemo(
    (): TableColumn<TAccountantReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
        minWidth: "160px",
      },
      {
        name: "Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
        minWidth: "150px",
      },
      {
        name: "Receipt Date",
        center: true,
        minWidth: "160px",
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
      },
      {
        name: "Status",
        center: true,
        minWidth: "190px",
        cell: (d) => (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
              d.receipt_status === "Payment Discrepancy"
                ? "bg-red-100 text-red-700 border-red-200"
                : "bg-green-100 text-green-700 border-green-200"
            }`}
          >
            {d.receipt_status}
          </span>
        ),
      },
      {
        name: "Receipt",
        center: true,
        minWidth: "150px",
        cell: (d) => (
          <div className="space-x-3">
            <ViewButton
              onClick={() => {
                if (d?.file_url) window.open(String(d.file_url), "_blank");
              }}
            />
            <DownloadButton
              onClick={() => {
                if (d?.file_url) window.open(String(d.file_url), "_blank");
              }}
            />
          </div>
        ),
      },
    ],
    []
  );

  const handleFileChange = (selected: File | null) => {
    if (!selected) {
      setFile(null);
      return;
    }

    if (!ACCEPTED_TYPES.includes(selected.type)) {
      toast.error("Only JPG, PNG and PDF files are allowed.");
      return;
    }

    if (selected.size > MAX_FILE_BYTES) {
      toast.error("File size too large. Maximum size is 10MB.");
      return;
    }

    setFile(selected);
  };

  const handleSubmit = () => {
    if (!file) {
      toast.warning("Please attach the official payment receipt.");
      return;
    }

    if (!receiptNo.trim()) {
      toast.warning("Please enter the receipt number.");
      return;
    }

    saveMutation.mutate(
      {
        applicationId,
        file,
        receiptNo: receiptNo.trim(),
        amountPaid,
        paymentDate,
        comment: comment.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            "Official receipt uploaded. Application forwarded to Level 1."
          );
          navigate("/accountant-receipts");
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to upload receipt"
          );
        },
      }
    );
  };

  if (!applicationId) {
    return (
      <div className="p-8 text-center text-gray-500">
        No application selected.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="space-y-6 mb-8">
        {/* PREVIOUS RECEIPTS — only shown on a correction re-upload */}
        {isCorrectionUpload && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-8">
            <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
              Previously Issued Receipts (Read-Only)
            </h4>

            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <DataTable
                data={[...(currentReceipt ? [currentReceipt] : []), ...previousReceipts]}
                keyField="accountant_receipt_id"
                columns={previousReceiptColumns}
                customStyles={tableCustomStyles}
              />
            </div>

            <p className="mt-4 text-sm text-gray-500 text-center">
              A new upload supersedes the receipt above; the earlier record is
              kept for audit and can still be viewed here.
            </p>
          </div>
        )}

        {/* UPLOAD FORM */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-8">
          <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
            Issue Official Payment Receipt
          </h4>

          {!!totalDemandAmount && (
            <p className="text-center text-sm text-gray-600 mb-6">
              Demand Note Amount:{" "}
              <strong>{totalDemandAmount.toFixed(2)} INR</strong>
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-sm md:text-base font-bold block mb-2">
                Receipt No. <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={receiptNo}
                placeholder="Enter receipt number"
                className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => setReceiptNo(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm md:text-base font-bold block mb-2">
                Amount (INR)
              </label>
              <input
                type="number"
                step="0.01"
                value={amountPaid}
                placeholder="Enter amount"
                className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => setAmountPaid(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm md:text-base font-bold block mb-2">
                Receipt Date
              </label>
              <input
                type="date"
                value={paymentDate}
                className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm md:text-base font-bold block mb-2">
                Receipt File <span className="text-red-500">*</span>
              </label>
              {/*
                The native file control is hidden and driven by an explicit
                button, matching how payNow.tsx renders its receipt picker — a
                bare <input type="file"> renders inconsistently here and does not
                match the rest of the app.
              */}
              <div className="flex items-stretch border border-gray-300 rounded-xl overflow-hidden">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-bold px-4 py-2.5 border-r border-gray-300"
                >
                  Choose file
                </button>
                <span
                  className={`grow px-3 py-2.5 text-sm truncate ${
                    file ? "text-gray-700" : "text-gray-400"
                  }`}
                  title={file?.name || ""}
                >
                  {file?.name || "No file chosen"}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                JPG, PNG or PDF, up to 10MB.
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm md:text-base font-bold block mb-2">
                Remarks
              </label>
              {/* Fixed height and resize disabled so the box cannot be dragged
                  smaller than its content or collapse as text is typed. */}
              <textarea
                rows={3}
                value={comment}
                placeholder="Add Comment"
                className="w-full h-24 resize-none p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 items-center border-t pt-5 mt-6">
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => navigate("/accountant-receipts")}
              className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>

            <button
              type="button"
              disabled={saveMutation.isPending || !file || !receiptNo.trim()}
              onClick={handleSubmit}
              className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {saveMutation.isPending
                ? "Uploading"
                : "Upload & Forward to Level 1"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountantReceiptUpload;
